import { useEffect, useImperativeHandle, useRef, forwardRef, type CSSProperties } from 'react';
import { colors } from '@classy/design-tokens';
import styles from './DrawingPad.module.css';

export interface DrawingPadHandle {
  clear(): void;
}

export interface DrawingPadProps {
  width: number;
  height: number;
  className?: string;
  /** Stroke color — defaults to dark text. */
  color?: string;
  /** Stroke width in CSS pixels. */
  lineWidth?: number;
  /** ARIA label for accessibility. */
  ariaLabel?: string;
  /** When true, render the pad shell with no canvas or pointer
   *  handlers. Teachers using a Promethean / ActivPanel pen overlay
   *  prefer this — their board's native annotation is faster and more
   *  precise than what the in-app canvas can offer. */
  disabled?: boolean;
}

/**
 * Pointer-Events-driven freehand canvas. Strokes are kept in canvas
 * memory only — clearing the canvas resets the pad. Drawing-pad state
 * is intentionally ephemeral: parent components hand a ref to call
 * `clear()` (e.g. on midnight rollover or page entry).
 *
 * `touch-action: none` + pointer capture eliminates the gap between
 * tap → first stroke that some touchscreens otherwise show.
 */
export const DrawingPad = forwardRef<DrawingPadHandle, DrawingPadProps>(function DrawingPad(
  {
    width,
    height,
    className,
    color = colors.textPrimary,
    lineWidth = 3,
    ariaLabel = 'Drawing pad',
    disabled = false,
  },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useImperativeHandle(ref, () => ({
    clear() {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    },
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
  }, [color, lineWidth]);

  function pointFor(canvas: HTMLCanvasElement, e: PointerEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }

  function onPointerDown(e: import('react').PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const native = e.nativeEvent;
    canvas.setPointerCapture(native.pointerId);
    const p = pointFor(canvas, native);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    canvas.dataset.drawing = 'true';
  }

  function onPointerMove(e: import('react').PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas || canvas.dataset.drawing !== 'true') return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const p = pointFor(canvas, e.nativeEvent);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  }

  function onPointerUp(e: import('react').PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (canvas.hasPointerCapture(e.nativeEvent.pointerId)) {
      canvas.releasePointerCapture(e.nativeEvent.pointerId);
    }
    canvas.dataset.drawing = 'false';
  }

  const style: CSSProperties = { width: `${width}px`, height: `${height}px` };

  if (disabled) {
    // Plain shell — same dimensions + frame as the live pad so the
    // page layout doesn't shift. No canvas, no listeners, no
    // crosshair cursor. The teacher's board pen draws on top via
    // the OS-level annotation overlay.
    return (
      <div
        className={
          className
            ? `${styles.pad} ${styles.padDisabled} ${className}`
            : `${styles.pad} ${styles.padDisabled}`
        }
        style={style}
        aria-label={ariaLabel}
        data-testid="drawing-pad"
        data-drawing-pad
        data-disabled="true"
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className={className ? `${styles.pad} ${className}` : styles.pad}
      width={width}
      height={height}
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerUp}
      aria-label={ariaLabel}
      data-testid="drawing-pad"
      data-drawing-pad
    />
  );
});
